import React, { useMemo, useState } from 'react';

const buildFriendlyResponseText = ({ title, selectedOptions, comment }) => {
    const labels = selectedOptions.map((option) => option.label).join(', ');
    if (comment) {
        return `${title}: ${labels}${labels ? ' | ' : ''}${comment}`;
    }

    return `${title}: ${labels}`;
};

const FeedbackQuestionMessage = ({ structuredData, labels, onSubmit }) => {
    const [selectedIds, setSelectedIds] = useState(() => structuredData?.submittedAnswer?.selectedOptionIds || []);
    const [comment, setComment] = useState(() => structuredData?.submittedAnswer?.comment || '');
    const [validationError, setValidationError] = useState('');
    const isSubmitted = Boolean(structuredData?.submitted);
    const isMultiple = structuredData?.selectionMode === 'multiple';

    const selectedOptions = useMemo(() => {
        const optionIds = new Set(selectedIds);
        return (structuredData?.options || []).filter((option) => optionIds.has(option.id));
    }, [selectedIds, structuredData]);

    const toggleOption = (optionId) => {
        if (isSubmitted) {
            return;
        }

        setValidationError('');
        setSelectedIds((previousIds) => {
            if (!isMultiple) {
                return [optionId];
            }

            const hasOption = previousIds.includes(optionId);
            if (hasOption) {
                return previousIds.filter((id) => id !== optionId);
            }

            const nextIds = [...previousIds, optionId];
            return nextIds.slice(0, structuredData?.maxSelections || nextIds.length);
        });
    };

    const handleSubmit = () => {
        const minSelections = structuredData?.required === false ? 0 : structuredData?.minSelections || 1;
        if (selectedIds.length < minSelections) {
            setValidationError(`${labels.selectAtLeast} ${minSelections} ${minSelections > 1 ? labels.options : labels.option}.`);
            return;
        }

        const responsePayload = {
            type: 'feedback_response',
            questionId: structuredData?.questionId || '',
            title: structuredData?.title || labels.feedback,
            selectedOptionIds: selectedIds,
            selectedOptions: selectedOptions.map((option) => ({ id: option.id, label: option.label })),
            comment: comment.trim()
        };

        onSubmit?.({
            responsePayload,
            displayContent: buildFriendlyResponseText({ title: structuredData?.title || labels.feedback, selectedOptions, comment: comment.trim() })
        });
    };

    return (
        <div className='copilot-chat-message copilot-chat-message--structured assistant'>
            <div className='copilot-chat-feedback-card'>
                <div className='copilot-chat-feedback-title'>{structuredData?.title}</div>
                {structuredData?.prompt && <div className='copilot-chat-feedback-prompt'>{structuredData.prompt}</div>}
                <div className='copilot-chat-feedback-options'>
                    {(structuredData?.options || []).map((option) => {
                        const isSelected = selectedIds.includes(option.id);
                        return (
                            <button
                                key={option.id}
                                type='button'
                                className={`copilot-chat-feedback-option ${isSelected ? 'is-selected' : ''}`}
                                onClick={() => toggleOption(option.id)}
                                disabled={option.disabled || isSubmitted}
                            >
                                <span className='copilot-chat-feedback-option-label'>{option.label}</span>
                                {option.description && <span className='copilot-chat-feedback-option-description'>{option.description}</span>}
                            </button>
                        );
                    })}
                </div>
                {structuredData?.allowComment &&
                    <textarea
                        className='copilot-chat-feedback-comment'
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        placeholder={structuredData?.commentPlaceholder || structuredData?.commentLabel}
                        rows={3}
                        disabled={isSubmitted}
                    />}
                {validationError && <div className='copilot-chat-feedback-error'>{validationError}</div>}
                {isSubmitted
                    ? <div className='copilot-chat-feedback-submitted'>{labels.feedbackSubmitted}</div>
                    : <button type='button' className='copilot-chat-feedback-submit' onClick={handleSubmit}>{structuredData?.submitLabel || labels.submit}</button>}
            </div>
        </div>
    );
};

export default FeedbackQuestionMessage;
